import { obterFabricaSerie } from '@componentes/charts/fabrica-serie-variavel';
import { DadosPrecipitacao } from '@componentes/charts/series/serie-precipitacao';
import { DadosPressao } from '@componentes/charts/series/serie-pressao';
import { DadosTemperatura } from '@componentes/charts/series/serie-temperatura';
import { DadosUmidade } from '@componentes/charts/series/serie-umidade';
import { DadosSerieVariavelMeteorologica } from '@componentes/charts/series/serie-variavel-meteorologica';
import { DadosVento } from '@componentes/charts/series/serie-vento';
import { DadosAgua } from '@componentes/charts/series/serie-agua';
import { DadosRadiacaoSolar } from '@componentes/charts/series/radiacao-solar';

export const VARIAVEIS_DIRECAO_VENTO = [
	'VENTO_DIRECAO_1',
	'VENTO_DIRECAO_2',
	'VENTO_DIRECAO_1_NA_MAXIMA',
	'VENTO_DIRECAO_2_NA_MAXIMA',
	'VENTO_DIRECAO_1_DESVIO_PADRAO',
	'VENTO_DIRECAO_2_DESVIO_PADRAO',
];

/**
 * Transforma uma resposta com dados de historico das variável para uma serie de uma variável especifica
 *
 * @param {string} variavel a variável desejada
 * @param {number} indexEixo o indice do eixo na qual a série será inserida no gráfico
 * @param {RespostaGraficoVariaveis} dados os dados de historico
 * @returns {DadosSerieVariavelMeteorologica} a serie a ser inserida no gráfico
 */
export function obterDadosSeriePorResultado(
	variavel: string,
	indexEixo: number,
	dados: any,
	periodo: string
): DadosSerieVariavelMeteorologica {
	const dadosGraficos = mapearDados(variavel, dados, periodo);
	const fabricaSerie = obterFabricaSerie(variavel);
	if (fabricaSerie && dadosGraficos && dadosGraficos.datas.length > 0) {
		return fabricaSerie.obterDadosSerie(dadosGraficos, indexEixo);
	}
	return { eixo: {}, serie: [] };
}

export function mapearDados(
	variavel: string,
	dados: any[],
	periodo: string
): any {
	const datas = dados.map(d => new Date(d.dataColeta));
	if (datas.length === 0 || datas.every(data => Number.isNaN(data.getTime()))) {
		return { datas: [] };
	}

	if (!variavel.includes('VENTO')) {
		if (
			dados
				.map(d => {
					if (variavel === 'PRECIPITACAO' || variavel === 'EVAPOTRANSPIRACAO') {
						return d.valorSomado;
					}
					return d.valor;
				})
				.every(value => value === null || value === undefined)
		) {
			return { datas: [] };
		}
	} else {
		const valoresVento = VARIAVEIS_DIRECAO_VENTO.includes(variavel)
			? dados.flatMap(d => [d.direcao, d.velocidade])
			: dados.map(d => d.valorVariavelMedia);
		if (valoresVento.every(value => value === null || value === undefined)) {
			return { datas: [] };
		}
	}
	switch (variavel) {
		case 'PRECIPITACAO':
		case 'EVAPOTRANSPIRACAO':
			return {
				datas,
				precipitacao: dados.map(d => d.valorSomado),
				periodo,
			} as DadosPrecipitacao;

		case 'CONTEUDO_AGUA_SOLO_10CM':
		case 'CONTEUDO_AGUA_SOLO_20CM':
		case 'CONTEUDO_AGUA_SOLO_30CM':
		case 'CONTEUDO_AGUA_SOLO_40CM':
		case 'CONTEUDO_AGUA_SOLO_50CM':
		case 'CONTEUDO_AGUA_SOLO_60CM':
			return {
				datas: datas,
				conteudo: dados.map(d => d.valor),
				periodo,
			} as DadosAgua;
		case 'PRESSAO_ATMOSFERICA':
			return {
				datas: datas,
				pressao: dados.map(d => d.valor),
				periodo,
			} as DadosPressao;

		case 'TEMPERATURA_DO_AR':
		case 'TEMPERATURA_MAXIMA':
		case 'TEMPERATURA_MINIMA':
		case 'TEMPERATURA_DO_SOLO':
		case 'TEMPERATURA_DO_SOLO_10CM':
		case 'TEMPERATURA_DO_SOLO_20CM':
		case 'TEMPERATURA_DO_SOLO_30CM':
		case 'TEMPERATURA_DO_SOLO_40CM':
		case 'TEMPERATURA_DO_SOLO_50CM':
		case 'TEMPERATURA_DO_SOLO_60CM':
			return {
				datas: datas,
				temperatura: dados.map(d => d.valor),
				periodo,
			} as DadosTemperatura;

		case 'UMIDADE_DO_AR':
		case 'UMIDADE_MAXIMA':
		case 'UMIDADE_MINIMA':
			return {
				datas: datas,
				umidade: dados.map(d => d.valor),
				periodo,
			} as DadosUmidade;

		case 'RADIACAO_SOLAR':
			return {
				datas: datas,
				radiacao: dados.map(d => d.valor),
				periodo,
			} as DadosRadiacaoSolar;
		case 'VENTO_DIRECAO_1_DESVIO_PADRAO':
		case 'VENTO_DIRECAO_2_DESVIO_PADRAO':
		case 'VENTO_DIRECAO_1':
		case 'VENTO_DIRECAO_2':
		case 'VENTO_DIRECAO_1_NA_MAXIMA':
		case 'VENTO_DIRECAO_2_NA_MAXIMA':
			return {
				datas,
				direcaoVento: dados.map(d => d.direcao),
				velocidadeVento: dados.map(d => d.velocidade),
				periodo,
			} as DadosVento;
		case 'VENTO_VELOCIDADE_1':
		case 'VENTO_VELOCIDADE_2':
		case 'VENTO_1_MAXIMA':
		case 'VENTO_2_MAXIMA':
		case 'VENTO_VELOCIDADE_1_MAXIMA':
		case 'VENTO_VELOCIDADE_2_MAXIMA':
			return {
				datas,
				velocidadeVento: dados.map(d => d.valorVariavelMedia),
				periodo,
			} as DadosVento;
		default:
			return { datas: [] };
	}
}

function normalizarData(d: any, periodo: string): number {
	const converterData = (valor: string): number => {
		const [ano, mes, dia] = valor.split('-').map(Number);

		return Date.UTC(ano, mes - 1, dia, 12);
	};

	if (periodo.toLowerCase() === 'mensal') {
		if (d.dataAgrupada) {
			if (d.dataAgrupada) {
				return converterData(d.dataAgrupada);
			}
		}
		return new Date(d.dataColeta).getTime();
	}

	if (periodo.toLowerCase() === 'diario') {
		if (d.dataAgrupada) {
			return converterData(d.dataAgrupada);
		}

		return new Date(d.dataColeta).getTime();
	}

	if (periodo.toLowerCase() === 'anual') {
		return Date.UTC(d.ano, d.mes - 1, 1, 12);
	}

	if (d.dataAgrupada) {
		return converterData(d.dataAgrupada);
	}

	return new Date(d.dataColeta).getTime();
}
